import { MinusIcon, PlusIcon } from '@heroicons/react/outline';
import { CollapseProps } from 'antd';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import PaginatedBookSlider from '@/components/BookComp/PaginatedBookSlider';
import HeroSection from '@/components/PlatformSubscription/HeroSection';
import LearnerJourneyBanner from '@/components/PlatformSubscription/LearnerJourneyBanner';
import AutoRenewalModal from '@/components/PlatformSubscription/Modals/AutoRenewalModal';
import PlanTable from '@/components/PlatformSubscription/PlanTable';
import { SubscriptionButtonTitle } from '@/components/PlatformSubscription/PlanTable/Columns';
import ReviewsSection from '@/components/PlatformSubscription/Reviews';
import Values from '@/components/PlatformSubscription/Values';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import { notify } from '@/components/shared/notify';

import { authModalControl } from '@/atoms/authModalControl';
import appwriteService from '@/config/appwrite';

import { user } from '../../atoms/user';
import SubscriptionModal from '../../components/PlatformSubscription/Modals/SubscriptionModal';
import { pageTitle } from '../../components/shared/basic/customHead';
import { ShouldRender } from '../../components/shared/basic/ShouldRender';
import CustomLoader from '../../components/shared/Loading';
import { plans } from '../../services/Subscribe';
import { IPlan } from '../../utils/interfaces/Subscription';

const AffiliateSubscription = () => {
  const router = useRouter();

  const { view } = router.query;
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [, setPlanActiveIndex] = useState<number>(2);
  const [loading, setLoading] = useState<boolean>(true);
  const [plan, setPlan] = useState<IPlan[]>();
  const [selectedPlan, setSelectedPlan] = useState<IPlan>();
  const [, setSelectedIndex] = useState<number>(0);
  const [, setSI] = useState<number>(2);
  const userState = useRecoilValue(user);
  const [isSubModalOpen, setIsSubModalOpen] = useState(false);
  const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);
  const [, setVisible] = useRecoilState(authModalControl);

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: 'What subscription plans are available?',
      children: (
        <div className='space-y-2 text-grayZl-500'>
          <p>
            12-Month Plan: Get full access to Audiobooks, VideoBooks, and Digital
            PDFs for 365 days.
          </p>
        </div>
      ),
    },
    {
      key: '2',
      label: 'What is included in my subscription?',
      children: (
        <div className='space-y-2 text-grayZl-500'>
          <p>Your subscription gives you:</p>
          <ul className='list-disc pl-5 space-y-2 md:text-sm text-xs'>
            <li>Unlimited access to all books for 365 days</li>
            <li>Unlimited listening to all Audiobooks for 365 days</li>
            <li>Unlimited learning through all VideoBooks for 365 days</li>
            <li>
              Access to all new books added during your subscription period
            </li>
            <li>
              Full access to all platform features throughout your subscription
            </li>
          </ul>
        </div>
      ),
    },
    {
      key: '3',
      label: 'Will my plan auto-renew?',
      children: (
        <p className='text-grayZl-500'>
          You can turn off auto-renewal anytime through your UPI or bank app
          settings.
        </p>
      ),
    },
    {
      key: '4',
      label: 'Can I cancel my subscription?',
      children: (
        <div className='space-y-2 text-grayZl-500'>
          <p>
            Yes, you can cancel your subscription anytime through the bank or
            UPI app you used to make the payment.
          </p>
          <ul className='list-disc pl-5 space-y-2 md:text-sm text-xs'>
            <li>
              You will continue to have access until your current subscription
              period ends.
            </li>
            <li>
              You will not be charged again for the next year unless you choose
              to restart your subscription.
            </li>
          </ul>
        </div>
      ),
    },
    {
      key: '5',
      label: 'Are subscription payments refundable?',
      children: (
        <div className='space-y-2 text-grayZl-500'>
          <p>
            No, subscription fees are non-refundable. However, your access will
            continue until the end of your active subscription period.
          </p>
          <p>
            If you face any issues, our support team is available 24×7 to assist
            you.
          </p>
        </div>
      ),
    },
  ];

  const subscriptionHandle = async (buttonTitle: SubscriptionButtonTitle) => {
    try {
      if (buttonTitle === SubscriptionButtonTitle.Cancel) {
        return setIsRenewModalOpen(true);
      }

      const loggedInStatus = await appwriteService.isLoggedIn();
      if (!loggedInStatus) {
        return setVisible((prevState) => ({
          ...prevState,
          signUp: true,
          noRedirect: true,
        }));
      }

      setIsSubModalOpen(true);
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    }
  };

  const plansHandle = async () => {
    try {
      setLoading(true);
      const result = await plans({ type: 'platform', group_name: 'affiliate' });
      setPlan(result.data);
      setSelectedPlan(result.data[result?.data?.length - 1]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAccordion = useCallback(
    (index: number) => {
      setActiveIndex(activeIndex === index ? -1 : index);
    },
    [activeIndex],
  );

  useEffect(() => {
    plansHandle();
  }, []);

  useEffect(() => {
    const planIndex = plan?.findIndex(
      (i) => i.id === userState?.subscription?.plan_id,
    );
    if (plan && planIndex && planIndex >= 0) {
      setSelectedIndex(plan.length - 1 - planIndex);
      setPlanActiveIndex(planIndex);
      setSI(planIndex);
    }
  }, [userState, plan]);

  return (
    <div>
      <Head>
        <title>{pageTitle('Subscribe')}</title>
      </Head>
      <HeroSection
        title={
          <>
            Become A ZebraLearn Prime
            <br className='hidden sm:inline' /> Member To Level Up Your Learning
          </>
        }
        subtitle='Get access to all zebralearn books.'
        showLearnerCommitment={true}
      />
      <ShouldRender check={!loading}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className='w-full'
        >
          <div className='mx-auto md:max-w-screen-md lg:max-w-screen-lg xl:max-w-7xl'>
            <PlanTable
              subscriptionHandle={subscriptionHandle}
              setSelectedPlan={setSelectedPlan}
              plans={plan}
            />
          </div>

          <LearnerJourneyBanner />

          <div className='mx-auto md:max-w-screen-md lg:max-w-screen-lg xl:max-w-7xl'>
            <div className='md:py-16 py-8'>
            <PaginatedBookSlider
              categoryId={null}
              categoryName='Top 10 Books of all time'
              isTopTen={true}
              hideShortBook={view?.toString() === 'cred'}
            />
            <PaginatedBookSlider
              categoryId={
                process.env.NEXT_PUBLIC_APP_ENV === 'staging'
                  ? '6634d1935c4cf495a9c52232'
                  : '6630ef434d8718482ab83ade'
              }
              categoryName='Finance'
              hideShortBook={view?.toString() === 'cred'}
            />
            <PaginatedBookSlider
              categoryId={
                process.env.NEXT_PUBLIC_APP_ENV === 'staging'
                  ? '6634d1745c4cf495a9c52209'
                  : '6630eef5c5f62d6df24ca7e8'
              }
              categoryName='Business'
              hideShortBook={view?.toString() === 'cred'}
            />
            <PaginatedBookSlider
              categoryId={
                process.env.NEXT_PUBLIC_APP_ENV === 'staging'
                  ? '6634d1105c4cf495a9c5213d'
                  : '6630ef444d8718482ab83ae0'
              }
              categoryName='Marketing'
              hideShortBook={view?.toString() === 'cred'}
            />
          </div>
          <Values />
          <ReviewsSection />

          <div className='mx-3 py-16 '>
            <p className='md:text-[32px] text-xl leading-[40px] font-semibold text-white md:mb-4 mb-0 text-left'>
              Frequently Asked Questions
            </p>
            <p className=' md:text-lg text-sm md:leading-[26px] leading-5  text-grayZl-400 text-left mb-12'>
              Everything you need to know about the product and billing.
            </p>
            <div className='flex flex-col gap-3 w-full'>
              {items.map((item, index) => (
                <motion.div
                  key={index}
                  className={clsx(
                    activeIndex === index ? 'bg-[#282829]' : 'bg-[#1C1C1C]',
                    'rounded-[8px] sm:rounded-[10px] overflow-hidden',
                  )}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.2 }}
                >
                  <motion.button
                    className='w-full flex items-center justify-between px-3 sm:px-4 md:px-4 lg:px-6 py-3 sm:py-3 md:py-3 lg:py-5 text-left text-white gap-5'
                    onClick={() => toggleAccordion(index)}
                  >
                    <span className='text-sm md:text-base/6 md:font-semibold font-medium'>
                      {item.label}
                    </span>
                    <motion.div
                      transition={{ duration: 0.2 }}
                    >
                      {activeIndex === index ? (
                        <MinusIcon className='h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-4 text-white' />
                      ) : (
                        <PlusIcon className='h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-4 text-[#FFFFFF99]' />
                      )}
                    </motion.div>
                  </motion.button>

                  <AnimatePresence>
                    {activeIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                          duration: 0.3,
                          ease: 'easeInOut',
                        }}
                        className='overflow-hidden'
                      >
                        <div className='px-4 sm:px-4 md:px-4 lg:px-6 py-3 sm:pb-3 md:pb-3 lg:pb-5 pt-0 text-sm md:text-xs lg:text-sm/6 font-normal text-grayZl-500'>
                          {item.children}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
        </motion.div>
      </ShouldRender>
      <ShouldRender check={loading}>
        <CustomLoader />
      </ShouldRender>
      <SubscriptionModal
        plan={selectedPlan}
        open={isSubModalOpen}
        setOpen={setIsSubModalOpen}
      />
      <AutoRenewalModal
        plan={selectedPlan}
        open={isRenewModalOpen}
        setOpen={setIsRenewModalOpen}
      />
    </div>
  );
};

export default AffiliateSubscription;
